import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { WALL_HEIGHT, type OfficeLayout, type RoomLayout, type WalkRect } from './layout';
import { CHAIR_SIZE, chairOffsetZ } from './furnitureLayout';

type Zone = 'it' | 'sales' | 'meeting' | 'accounting' | 'development' | 'break' | 'server' | 'general';
const zones: Record<string, Zone> = {
  'is-office': 'it', 'sales-1f': 'sales', 'meeting-1f': 'meeting',
  'keiri-2f': 'accounting', 'dev-2f': 'development', 'break-2f': 'break', 'server-room': 'server',
};
const accents: Record<Zone, number> = {
  it: 0x447d89, sales: 0xb1824d, meeting: 0x758b70, accounting: 0x586c92,
  development: 0x827097, break: 0x619a8b, server: 0x74818c, general: 0x8b969b,
};
const floorAccents = [0xb1824d, 0x619a8b, 0x447d89];

/** A repeating, translucent finish; no image request or per-tile geometry. */
function patternMaterial(color: number, width: number, height: number, grid: boolean): THREE.MeshStandardMaterial {
  const size = 32;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = (y * size + x) * 4;
    data[i] = (color >> 16) & 255; data[i + 1] = (color >> 8) & 255; data[i + 2] = color & 255;
    data[i + 3] = grid ? (x < 1 || y < 1 ? 100 : 12) : ((x < 16) !== (y < 16) ? 45 : 8);
  }
  const map = new THREE.DataTexture(data, size, size);
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  map.repeat.set(width / 0.6, height / 0.6);
  map.magFilter = THREE.LinearFilter;
  map.minFilter = THREE.LinearMipmapLinearFilter;
  map.generateMipmaps = true;
  map.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ map, transparent: true, depthWrite: false, roughness: 0.9,
    polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
}

function addFloorPattern(root: THREE.Group, rect: WalkRect, color: number, grid: boolean): void {
  const w = rect.x1 - rect.x0, d = rect.z1 - rect.z0;
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(w, d), patternMaterial(color, w, d, grid));
  floor.material.opacity = 1 / 3;
  floor.name = `zone-floor:${rect.id}`;
  floor.rotation.x = -Math.PI / 2;
  floor.position.set((rect.x0 + rect.x1) / 2, rect.y + 0.003, (rect.z0 + rect.z1) / 2);
  floor.receiveShadow = true;
  root.add(floor);
}

function addRoomZone(root: THREE.Group, room: RoomLayout): void {
  const zone = zones[room.id] ?? 'general';
  const color = accents[zone];
  const group = new THREE.Group();
  group.name = `zone-wall:${room.id}`;
  group.userData.zone = zone;
  // Local +Z faces the room. Every solid ends at most 0.20m from the wall axis.
  group.position.set(room.rect.x0, room.y, room.center[2]);
  group.rotation.y = Math.PI / 2;
  root.add(group);
  const w = Math.min(3.2, room.depth - 0.6);
  addBox(group, room.depth - 0.1, 0.14, 0.04, color, 0, 0.08, 0.17).name = 'zone-skirt';
  const panel = (name: string, color: number, height = 1.25) => {
    const mesh = addBox(group, w, height, 0.035, color, 0, 1.85, 0.17);
    mesh.name = name;
    return mesh;
  };
  const label = (text: string) => {
    const sign = addBox(group, 0.8, 0.22, 0.025, 0x263c47, 0, 2.64, 0.175);
    sign.userData.signText = text;
  };
  if (zone === 'sales' || zone === 'accounting') {
    panel(zone === 'sales' ? 'sales-organizer' : 'accounting-archive', zone === 'sales' ? 0xb89b75 : 0x7e8b9d);
    for (const y of [1.3, 1.85, 2.4]) addBox(group, w, 0.035, 0.045, color, 0, y, 0.175);
    for (const x of [-w / 3, 0, w / 3]) {
      addBox(group, zone === 'sales' ? 0.25 : 0.65, 0.4, 0.012,
        zone === 'sales' ? 0xe8dfc7 : 0xc7cdd4, x, 1.61, 0.192);
    }
    label(zone === 'sales' ? '営業資料' : '帳票保管');
  } else if (zone === 'meeting' || zone === 'development' || zone === 'it') {
    panel(zone === 'meeting' ? 'meeting-whiteboard' : zone === 'development' ? 'development-taskboard' : 'it-network-board',
      zone === 'meeting' ? 0xf0f1e9 : 0x344952);
    if (zone === 'meeting') {
      addBox(group, w * 0.85, 0.025, 0.045, color, 0, 1.23, 0.175);
      for (let i = 0; i < 3; i++) addBox(group, 0.12, 0.018, 0.012, [0x365b85, 0x9e554d, 0x30383e][i], -0.3 + i * 0.2, 1.25, 0.188);
      label('会議メモ');
    } else if (zone === 'it') {
      // A small topology diagram: backbone with three connected access nodes.
      addBox(group, w * 0.7, 0.018, 0.008, color, 0, 1.85, 0.192);
      for (const x of [-w / 3, 0, w / 3]) {
        addBox(group, 0.018, 0.36, 0.008, color, x, 1.68, 0.192);
        addBox(group, w * 0.2, 0.23, 0.008, 0x99c1ba, x, 1.5, 0.192);
      }
      addBox(group, w * 0.25, 0.23, 0.008, 0xaebdd8, 0, 1.96, 0.192);
      label('ネットワーク構成');
    } else {
      for (const x of [-w / 6, w / 6]) addBox(group, 0.015, 1.1, 0.008, color, x, 1.85, 0.192);
      for (let i = 0; i < 6; i++) addBox(group, w * 0.19, 0.23, 0.008,
        [0xd5c692, 0x99c1ba, 0xaebdd8][i % 3], (i % 3 - 1) * w / 3, 2.12 - Math.floor(i / 3) * 0.48, 0.192);
      label('未着手 ／ 作業中 ／ 完了');
    }
  } else if (zone === 'break') {
    const tile = panel('break-wall-tiles', 0xd0e1d8, 1.5);
    const joints = new THREE.Mesh(new THREE.PlaneGeometry(w, 1.5), patternMaterial(color, w, 1.5, true));
    joints.position.set(0, 1.85, 0.19);
    group.add(joints);
    (tile.material as THREE.MeshStandardMaterial).roughness = 0.25;
    label('休憩スペース');
  } else if (zone === 'server') {
    const metal = panel('server-metal-panels', 0x7e8992, 1.65);
    const material = metal.material as THREE.MeshStandardMaterial;
    material.metalness = 0.8; material.roughness = 0.35;
    for (const x of [-w / 3, 0, w / 3]) addBox(group, 0.012, 1.6, 0.008, 0x344049, x, 1.85, 0.192);
    label('サーバー室');
  }
  addFloorPattern(root, room.rect, color, zone === 'break' || zone === 'server' || zone === 'accounting');
}

/** Restore the exterior view and the existing storage footprint, including hall rooms. */
function addBackWall(root: THREE.Group, room: RoomLayout): void {
  if (/eps|server|stairs/.test(room.id)) return;
  const north = room.center[2] < 0;
  const group = new THREE.Group();
  group.name = `back-wall:${room.id}`;
  group.position.set(room.center[0], room.y, north ? room.rect.z0 : room.rect.z1);
  if (!north) group.rotation.y = Math.PI;
  root.add(group);
  const width = room.width - 1.1;
  const window = new THREE.Mesh(new THREE.PlaneGeometry(width, 1.65),
    new THREE.MeshBasicMaterial({ color: 0xc7d7e0, toneMapped: false }));
  window.name = 'city-view';
  window.position.set(0, 1.92, 0.16);
  group.add(window);
  for (const x of [-width / 2, 0, width / 2]) addBox(group, 0.045, 1.72, 0.03, 0x8b969b, x, 1.92, 0.18);
  for (const y of [1.06, 2.78]) addBox(group, width + 0.08, 0.045, 0.03, 0x909a9f, 0, y, 0.18);
  // Open blinds share one geometry/material/draw call per room.
  const blinds = new THREE.InstancedMesh(new THREE.BoxGeometry(width, 0.015, 0.025),
    new THREE.MeshStandardMaterial({ color: 0xd6d8d6, roughness: 0.8 }), 8);
  blinds.name = 'window-blinds';
  const transform = new THREE.Object3D();
  for (let i = 0; i < blinds.count; i++) {
    transform.position.set(0, 2.66 - i * 0.08, 0.18);
    transform.rotation.x = -0.3;
    transform.updateMatrix();
    blinds.setMatrixAt(i, transform.matrix);
  }
  blinds.instanceMatrix.needsUpdate = true;
  group.add(blinds);
  // Match obstacles.ts exactly; this restored furniture is distinct from new 0.2m wall decorations.
  const storageWidth = Math.min(2.4, width);
  addBox(group, storageWidth, 0.79, 0.22, 0xc9ccca, 0, 0.4, 0.195).name = `storage:${room.id}`;
  for (const x of [-storageWidth * 0.2, storageWidth * 0.2]) {
    addBox(group, 0.028, 0.12, 0.012, 0x71797d, x, 0.57, 0.299);
  }
}

/** Decoration only: no walk rectangles, obstacles, furniture or pick targets are added. */
export function buildOfficeDetails(layout: OfficeLayout): THREE.Group {
  const root = new THREE.Group();
  root.name = 'office-architecture';
  for (const room of Object.values(layout.rooms)) {
    const { x0, x1, z0, z1 } = room.rect;
    const north = room.center[2] < 0;
    addRoomZone(root, room);
    addBackWall(root, room);

    // Acoustic ceiling tile joints and a recessed air conditioning cassette.
    for (let x = x0 + 0.6; x < x1; x += 1.2) addBox(root, 0.012, 0.008, room.depth, 0xc8cac7, x, room.y + WALL_HEIGHT - 0.066, room.center[2]);
    for (let z = z0 + 0.6; z < z1; z += 1.2) addBox(root, room.width, 0.008, 0.012, 0xc8cac7, room.center[0], room.y + WALL_HEIGHT - 0.066, z);
    const airX = room.center[0] - room.width * 0.22;
    addBox(root, 0.78, 0.06, 0.78, 0xdadbd6, airX, room.y + WALL_HEIGHT - 0.11, room.center[2]);
    for (let i = 0; i < 9; i++) addBox(root, 0.55, 0.012, 0.015, 0x656e71, airX, room.y + WALL_HEIGHT - 0.145, room.center[2] - 0.25 + i * 0.06);

    // Classroom-style blade sign: readable along the corridor from either direction.
    const inward = north ? 1 : -1;
    const signX = room.doorPosition[0] + 1.6;
    const signY = room.y + 2.35;
    const wallZ = room.doorPosition[2];
    addBox(root, 0.12, 0.36, 0.04, 0x929c9c, signX, signY, wallZ + inward * 0.17).name = `room-sign-mount:${room.id}`;
    const nameplate = addBox(root, 0.96, 0.28, 0.035, 0x263c47, signX, signY, wallZ + inward * 0.68);
    nameplate.name = `room-sign:${room.id}`;
    nameplate.rotation.y = Math.PI / 2;
    nameplate.userData.signText = `${room.floor}F  ${room.name}`;
    nameplate.userData.signDoubleSided = true;
    nameplate.userData.signSize = [0.96, 0.28, 0.035];
  }
  for (const corridor of layout.walkRects.filter(rect => rect.kind === 'corridor')) {
    const color = floorAccents[((corridor.floor - 1) % floorAccents.length + floorAccents.length) % floorAccents.length];
    addFloorPattern(root, corridor, color, true);
    const group = new THREE.Group();
    group.name = `zone-corridor:${corridor.floor}`;
    root.add(group);
    // Existing wall segments already exclude door gaps. Do not bridge them with rails.
    for (const wall of layout.walls) {
      if (wall.y !== corridor.y || wall.z0 !== wall.z1 ||
        (wall.z0 !== corridor.z0 && wall.z0 !== corridor.z1)) continue;
      const x0 = Math.max(wall.x0, corridor.x0), x1 = Math.min(wall.x1, corridor.x1);
      if (x1 - x0 < 0.3) continue;
      const inward = wall.z0 === corridor.z0 ? 1 : -1;
      const x = (x0 + x1) / 2;
      addBox(group, x1 - x0 - 0.08, 0.09, 0.04, color, x, wall.y + 2.35, wall.z0 + inward * 0.17).name = 'corridor-accent';
      addBox(group, x1 - x0 - 0.12, 0.055, 0.045, 0x929c9c, x, wall.y + 0.95, wall.z0 + inward * 0.175).name = 'corridor-handrail';
    }
    const sign = addBox(group, 0.8, 0.22, 0.025, 0x263c47, corridor.x0 + 0.17, corridor.y + 1.8, 0);
    sign.rotation.y = Math.PI / 2;
    sign.userData.signText = `${corridor.floor}F\u3000階段は廊下の奥`;
  }
  return root;
}

export function addBox(root: THREE.Object3D, w: number, h: number, d: number, color: number,
  x: number, y: number, z: number): THREE.Mesh {
  const mesh = new THREE.Mesh(new RoundedBoxGeometry(w, h, d, 2, Math.min(0.012, w / 4, h / 4, d / 4)),
    new THREE.MeshStandardMaterial({ color, roughness: 0.72, metalness: 0.08 }));
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  root.add(mesh);
  return mesh;
}

/** Chair position is also represented in obstacles.ts, separate from the desk. */
export function addWorkstationDetails(root: THREE.Group, deskW: number, deskD: number, insetItems = false): void {
  const chair = new THREE.Group();
  chair.name = 'asset-slot:chair';
  chair.userData.assetSize = [...CHAIR_SIZE];
  chair.userData.assetOffset = [0, 0, 0];
  chair.position.set(0, -0.75, chairOffsetZ(deskD));
  addBox(chair, 0.43, 0.07, 0.39, 0x262c30, 0, 0.43, 0);
  addBox(chair, 0.045, 0.32, 0.045, 0x646b70, 0, 0.23, 0);
  const back = addBox(chair, 0.4, 0.44, 0.055, 0x313a3e, 0, 0.69, 0.145);
  back.rotation.x = -0.07;
  for (let i = 0; i < 10; i++) addBox(chair, 0.34, 0.005, 0.005, 0x5c666b, 0, 0.51 + i * 0.035, 0.179);
  for (let i = 0; i < 5; i++) {
    const a = i * Math.PI * 2 / 5;
    const spoke = addBox(chair, 0.24, 0.03, 0.035, 0x373f43, Math.cos(a) * 0.1, 0.065, Math.sin(a) * 0.1);
    spoke.rotation.y = -a;
    addBox(chair, 0.055, 0.055, 0.055, 0x1d2326, Math.cos(a) * 0.21, 0.033, Math.sin(a) * 0.21);
  }
  root.add(chair);
  const side = deskW / 2 - 0.15 - (insetItems ? 0.075 : 0);
  const paperZ = insetItems ? -0.03 : -0.08;
  addBox(root, 0.2, 0.013, 0.26, 0xeeeade, side, 0.018, paperZ).name = 'desk-memo';
  addBox(root, 0.18, 0.008, 0.23, 0xf8f6ec, side + 0.006, 0.028, paperZ + 0.005).name = 'desk-memo-top';
  const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.03, 0.095, 20),
    new THREE.MeshStandardMaterial({ color: 0xdcdedb, roughness: 0.3 }));
  cup.position.set(-side, 0.05, 0.03);
  cup.name = 'desk-cup';
  root.add(cup);
  // Cable tray and a monitor lead below the desktop.
  addBox(root, deskW * 0.64, 0.06, 0.1, 0x515b61, 0, -0.14, -deskD * 0.3);
  addCable(root, [[0, 0.08, -0.08], [0.12, -0.1, -0.21], [0.2, -0.35, -0.2], [-0.25, -0.52, -0.2]], 0x222a30, 0.008);
}

export function addCable(root: THREE.Object3D, points: number[][], color: number, radius = 0.004): void {
  const curve = new THREE.CatmullRomCurve3(points.map(p => new THREE.Vector3(p[0], p[1], p[2])));
  const mesh = new THREE.Mesh(new THREE.TubeGeometry(curve, 20, radius, 6, false),
    new THREE.MeshStandardMaterial({ color, roughness: 0.65 }));
  mesh.castShadow = true;
  root.add(mesh);
}

/** Browser-only image loading; geometry stays independently testable without a DOM. */
export class OfficeImageMaterials {
  private textures = new Set<THREE.Texture>();
  private disposed = false;
  private compressed: KTX2Loader | null = null;

  apply(root: THREE.Object3D, renderer?: THREE.WebGLRenderer): void {
    const loader = new THREE.TextureLoader();
    if (renderer) {
      this.compressed = new KTX2Loader().setTranscoderPath(`${import.meta.env.BASE_URL}assets/basis/`);
      this.compressed.detectSupport(renderer);
    }
    const load = (name: string, use: (texture: THREE.Texture) => void): void => {
      const original = `${import.meta.env.BASE_URL}assets/realism/${name}`;
      const pending = this.compressed
        ? this.compressed.loadAsync(original.replace(/\.png$/, '.ktx2')).then(texture => {
          // Our KTX files store top-left rows. Compressed uploads cannot use
          // UNPACK_FLIP_Y_WEBGL, so reproduce TextureLoader's flip in UV space.
          texture.repeat.y = -1;
          texture.offset.y = 1;
          return texture;
        }).catch(() => {
          if (this.disposed) throw new Error('Disposed');
          console.warn(`[office] KTX2読込失敗: ${name}、PNGで継続`);
          return loader.loadAsync(original);
        }) : loader.loadAsync(original);
      void pending.then(texture => {
        if (this.disposed) { texture.dispose(); return; }
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = 4;
        this.textures.add(texture);
        use(texture);
        root.userData.compressedTextures = (root.userData.compressedTextures ?? 0) + (texture instanceof THREE.CompressedTexture ? 1 : 0);
      }).catch(error => { if (!this.disposed) console.warn(`[office] 画像を読み込めません: ${name}`, error); });
    };
    load('oak-albedo.png', texture => {
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      root.traverse(object => {
        if (!(object instanceof THREE.Mesh)) return;
        const mat = object.material;
        if (mat instanceof THREE.MeshStandardMaterial && mat.userData.oak) {
          mat.map = texture;
          mat.color.setHex(0xffffff);
          mat.roughness = 0.65;
          mat.needsUpdate = true;
        }
      });
    });
    load('city-view.png', texture => {
      root.traverse(object => {
        if (object instanceof THREE.Mesh && object.name === 'city-view') {
          const mat = object.material as THREE.MeshBasicMaterial;
          mat.map = texture;
          mat.color.setHex(0xffffff);
          mat.needsUpdate = true;
        }
      });
    });
    load('carpet-albedo.png', texture => {
      root.traverse(object => {
        if (!(object instanceof THREE.Mesh)) return;
        const mat = object.material;
        if (!(mat instanceof THREE.MeshStandardMaterial) || !mat.userData.carpetSize) return;
        const [width, depth] = mat.userData.carpetSize as [number, number];
        const tiled = texture.clone();
        tiled.wrapS = tiled.wrapT = THREE.RepeatWrapping;
        tiled.repeat.set(width / 0.6, depth / 0.6 * texture.repeat.y);
        tiled.needsUpdate = true;
        this.textures.add(tiled);
        mat.map?.dispose();
        mat.map = tiled;
        mat.roughness = 1;
        mat.needsUpdate = true;
      });
    });
    root.traverse(object => {
      if (!(object instanceof THREE.Mesh) || !object.userData.signText) return;
      for (const previous of [...object.children]) {
        if (!(previous instanceof THREE.Mesh) || !previous.name.startsWith('office-room-sign-face')) continue;
        previous.geometry.dispose();
        (previous.material as THREE.Material).dispose();
        object.remove(previous);
      }
      const canvas = document.createElement('canvas');
      canvas.width = 768; canvas.height = 192;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#263c47'; ctx.fillRect(0, 0, 768, 192);
      ctx.fillStyle = '#f3f1e7';
      ctx.font = object.userData.signDoubleSided ? 'bold 72px sans-serif' : '44px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(object.userData.signText), 384, 96, 730);
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      this.textures.add(tex);
      const [width, height, depth] = (object.userData.signSize ?? [0.8, 0.22, 0.025]) as [number, number, number];
      const sides = object.userData.signDoubleSided ? [1, -1] : [object.userData.signSouth ? -1 : 1];
      for (const side of sides) {
        const face = new THREE.Mesh(new THREE.PlaneGeometry(width, height), new THREE.MeshBasicMaterial({ map: tex }));
        face.name = side === 1 ? 'office-room-sign-face' : 'office-room-sign-face-back';
        face.position.z = side * (depth / 2 + 0.002);
        if (side === -1) face.rotation.y = Math.PI;
        object.add(face);
      }
    });
  }

  dispose(): void {
    this.disposed = true;
    this.compressed?.dispose();
    for (const texture of this.textures) texture.dispose();
    this.textures.clear();
  }
}
